import React, { useEffect, useState, useCallback } from 'react';
import { useDesignContext } from '../../hooks/useDesignContext';
import styled, { keyframes } from 'styled-components';
import { API_BASE_URL } from '../../config/api';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LocalSpinner = styled.div<{ $size?: number }>`
  width: ${props => props.$size || 24}px;
  height: ${props => props.$size || 24}px;
  border: 3px solid rgba(93, 114, 44, 0.2);
  border-top-color: #5D722C;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CenterLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  color: #5D722C;
  font-size: 13px;
  font-weight: 500;
  gap: 12px;
`;

// Styled Components
const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const VaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-top: 10px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const VaseItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 13px;
  color: #374151;
`;

const VaseImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  cursor: pointer;
  margin-bottom: 6px;
  border-radius: 4px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: scale(1.05);
    border-color: #5D722C;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-weight: 600;
  color: #333333;
`;

// Interfaces
interface VaseEntry {
  id: number;
  name: string;
  image_path: string;
  thumb_path: string;
  vase_id: string;
}

interface VasesProps {
  onClose: () => void;
  onVaseSelect: (vase: VaseEntry) => void;
  esqModalVasePath?: string;
}

// Main Component – data comes exclusively from the designs table via fetchDesignPreview
const Vases: React.FC<VasesProps> = ({ onClose, onVaseSelect, esqModalVasePath }) => {
  const { currentDesign, selectedGraniteMeta, fetchDesignPreview } = useDesignContext();

  const [vase, setVase] = useState<VaseEntry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setVase(null);

      // Early escape for ESQ mode using the passed-in vase path
      if (esqModalVasePath && esqModalVasePath.trim().length > 0) {
        const vaseVariant = /[Aa]v02/.test(esqModalVasePath) ? 'AV02' : 'AV01';
        if (!cancelled) {
          setVase({
            id: 0,
            name: vaseVariant,
            vase_id: vaseVariant,
            image_path: esqModalVasePath,
            thumb_path: esqModalVasePath,
          });
          setLoading(false);
        }
        return;
      }

      const modelId = Number(
        selectedGraniteMeta?.modelId ?? (currentDesign as any)?.selectedModelId ?? 0
      );
      const sizeId = Number(
        selectedGraniteMeta?.sizeId ?? (currentDesign as any)?.selectedSizeId ?? 0
      );
      const colorId = Number(
        selectedGraniteMeta?.colorId ?? (currentDesign as any)?.selectedColorId ?? 0
      );

      if (!modelId || !sizeId || !colorId) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        // fetchDesignPreview is cached in the context – no extra network cost
        const data = await fetchDesignPreview(modelId, sizeId, colorId);
        const vaseImg: string | null = data?.data?.vase_image ?? null;
        // Derive variant from the image path (Av02 → AV02, anything else → AV01).
        const vaseVariant: string = vaseImg && /[Aa]v02/.test(vaseImg) ? 'AV02' : 'AV01';

        if (!cancelled && vaseImg && typeof vaseImg === 'string' && vaseImg.trim()) {
          setVase({
            id: 0,
            name: vaseVariant,
            vase_id: vaseVariant,
            image_path: vaseImg,
            thumb_path: vaseImg,
          });
        }
      } catch {
        // Nothing to show
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [
    esqModalVasePath,
    selectedGraniteMeta?.modelId,
    selectedGraniteMeta?.sizeId,
    selectedGraniteMeta?.colorId,
    (currentDesign as any)?.selectedModelId,
    (currentDesign as any)?.selectedSizeId,
    (currentDesign as any)?.selectedColorId,
    fetchDesignPreview,
  ]);

  const getImageUrl = useCallback((imagePath: string) => {
    const cleanPath = imagePath.replace(/^uploads[\\\/]/, '');
    return `${API_BASE_URL}/api/uploads/${cleanPath}`;
  }, []);

  const handleVaseClick = useCallback(() => {
    if (vase) onVaseSelect(vase);
  }, [vase, onVaseSelect]);

  if (loading) {
    return (
      <CenterLoader>
        <LocalSpinner $size={32} />
        <span>Loading vases...</span>
      </CenterLoader>
    );
  }

  if (!vase) {
    return <EmptyState>No vase available for this model.</EmptyState>;
  }

  return (
    <FormGroup>
      <Header>
        <div>Vases</div>
      </Header>
      <VaseGrid>
        <VaseItem>
          <VaseImage
            src={getImageUrl(vase.thumb_path || vase.image_path)}
            alt={vase.name}
            onClick={handleVaseClick}
          />
          <div>{vase.name}</div>
        </VaseItem>
      </VaseGrid>
    </FormGroup>
  );
};

export default Vases;
